import { useState, useEffect } from "react";
import { Box, Typography, IconButton, Paper } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";

const ImageCarousel = ({ images, title }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(3);
  const [itemSize, setItemSize] = useState(120); // Default square size

  useEffect(() => {
    const updateLayout = () => {
      const width = window.innerWidth;

      // Determine number of images based on screen width
      if (width < 600) {
        setItemsPerPage(1);
        setItemSize(150);
      } else if (width < 900) {
        setItemsPerPage(2);
        setItemSize(170);
      } else if (width < 1200) {
        setItemsPerPage(3);
        setItemSize(220);
      } else {
        setItemsPerPage(Math.floor(width / (itemSize + 20))); // Fit as many as possible
        setItemSize(Math.min(200, Math.floor(width / itemsPerPage) - 20)); // Keep square shape
      }
    };

    updateLayout();
    window.addEventListener("resize", updateLayout);
    return () => window.removeEventListener("resize", updateLayout);
  }, [itemSize, itemsPerPage]);

  const totalPages = Math.ceil(images.length / itemsPerPage);
  const lastPageIndex = (totalPages - 1) * itemsPerPage;

  const nextPage = () => {
    setCurrentIndex((prevIndex) => Math.min(prevIndex + itemsPerPage, lastPageIndex));
  };

  const prevPage = () => {
    setCurrentIndex((prevIndex) => Math.max(prevIndex - itemsPerPage, 0));
  };

  const activePage = Math.round(currentIndex / itemsPerPage);

  return (
    <Paper elevation={3} sx={{ padding: 3, width: "80%", margin: "auto", textAlign: "center" }}>
      {/* Title */}
      <Typography variant="h5" gutterBottom>{title}</Typography>

      {/* Carousel Wrapper */}
      <Box display="flex" alignItems="center" justifyContent="center">
        {/* Left Chevron */}
        <IconButton onClick={prevPage} disabled={currentIndex === 0}>
          <ChevronLeft fontSize="large" />
        </IconButton>

        {/* Image Items (Always Square) */}
        <Box display="flex" gap={2} overflow="hidden">
          {images.slice(currentIndex, currentIndex + itemsPerPage).map((image, index) => (
            <Box
              key={index}
              width={itemSize}
              height={itemSize}
              display="flex"
              alignItems="center"
              justifyContent="center"
              border="2px solid #ccc"
              sx={{ borderRadius: 2, overflow: "hidden" }}
            >
              <img
                src={image}
                alt={`carousel item ${index}`}
                loading="lazy"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block"
                }}
              />
            </Box>
          ))}
        </Box>

        {/* Right Chevron */}
        <IconButton onClick={nextPage} disabled={currentIndex + itemsPerPage >= images.length}>
          <ChevronRight fontSize="large" />
        </IconButton>
      </Box>

      {/* Breadcrumb Indicators */}
      <Box display="flex" justifyContent="center" marginTop={1}>
        {Array.from({ length: totalPages }).map((_, page) => (
          <Box
            key={page}
            width={12}
            height={12}
            borderRadius="50%"
            bgcolor={activePage === page ? "black" : "#ccc"}
            margin={0.5}
          />
        ))}
      </Box>
    </Paper>
  );
};

export default ImageCarousel;
